import { locales } from "@/i18n/config"

/**
 * How each locale the site serves writes a decimal separator.
 *
 * Hand-rolled rather than `Intl`/`toLocaleString`, for the same reason
 * `formatCalories` on the BMR widget is: their output depends on the ICU data
 * the runtime happens to ship (Node under vitest, a browser in production),
 * which would make the rendered text, and therefore every e2e assertion on
 * it, environment-dependent.
 *
 * Keyed on locale alone, which is sound while every caller is a CH-gated page:
 * `de` here always means de-CH. A German or Austrian market would need the
 * country too, since Germany groups thousands with a dot where Switzerland
 * uses an apostrophe.
 *
 * The key set is exactly `locales` from `i18n/config`, enforced by
 * `__tests__/locale-number-format.test.ts`, so a lookup cannot miss for any
 * locale the router can produce. `strict` is on but `noUncheckedIndexedAccess`
 * is not, so that test is what stands between this and a silently English
 * decimal point on a French page.
 */
const DECIMAL_SEPARATOR: Record<string, string> = {
  de: ",",
  en: ".",
  fr: ",",
  nl: ",",
}

/**
 * How each locale groups thousands.
 *
 * The apostrophe for `de` and `fr` is the Swiss convention, and it is read off
 * the catalogs rather than assumed: `de.json` writes it 20 times and `fr.json`
 * 16, with zero comma-grouped figures between them. It is not cosmetic. A
 * German reader parses `1,730` as one point seven three, so grouping a kcal
 * figure with a comma changes the number the page appears to state by a factor
 * of a thousand.
 *
 * `en` keeps the comma the BMR widget already emitted, so the English page and
 * its e2e assertions are unchanged by this module arriving.
 */
const GROUP_SEPARATOR: Record<string, string> = {
  de: "'",
  en: ",",
  fr: "'",
  nl: ".",
}

/** Every locale this module claims to cover. Exported for its own test. */
export const FORMATTED_LOCALES = Object.keys(DECIMAL_SEPARATOR)

/**
 * A number at a fixed number of decimals, written the way `locale` writes it.
 *
 * `formatDecimal(22.94, 1, "de")` is `"22,9"`. Rounding is `toFixed`'s, which
 * is what the callers already displayed, so switching a page to this function
 * changes the separator and nothing else.
 */
export function formatDecimal(
  value: number,
  digits: number,
  locale: string
): string {
  return value.toFixed(digits).replace(".", DECIMAL_SEPARATOR[locale])
}

/**
 * A whole number with the locale's thousands grouping: 1730 becomes `"1'730"`
 * in de and fr, `"1,730"` in en.
 */
export function formatInteger(value: number, locale: string): string {
  return String(Math.round(value)).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    GROUP_SEPARATOR[locale]
  )
}

/**
 * A number written as-is, with only the decimal separator localised and no
 * grouping: 1.375 becomes `"1,375"` in de, and 1.2 stays `"1,2"` rather than
 * being padded to `"1,200"`.
 *
 * This is what the activity multipliers need: `formatDecimal` would have to
 * fix a digit count, and the five factors do not share one.
 */
export function formatNumber(value: number, locale: string): string {
  return String(value).replace(".", DECIMAL_SEPARATOR[locale])
}

/** True when `locales` from config is fully covered. Used by the test. */
export function coversEverySiteLocale(): boolean {
  return locales.every(
    (locale) => locale in DECIMAL_SEPARATOR && locale in GROUP_SEPARATOR
  )
}
